import React from 'react'
import { BreadcrumbJsonLd, NextSeo } from 'next-seo';
import NotSure from '../components/NotSure';
import tentSitePlanning from '../public/Pictures/categories-v2/party-tent-site-planning.jpg'
import ProductList from '../components/ProductList';
import { useDispatch, useSelector } from 'react-redux';
import { packagesReceived } from '../store/slices/entities/packages';
import { wrapper } from '../store/configStore';
import { SERVER_IP } from '../store/middleware/api';
import { SHOW_POPUP } from '../store/slices/ui';
import axios from 'axios';
import ClientReviews from '../components/ClientReviews';
import PastEvents from '../components/PastEvents';
import LandingCategories from '../components/LandingCategories';
import { getFilteredProducts, productsReceived, productsSetFilters } from '../store/slices/entities/products';
import { AiOutlineColumnHeight } from '@react-icons/all-files/ai/AiOutlineColumnHeight';
import { MdGroup } from '@react-icons/all-files/md/MdGroup';
import { AiOutlineFullscreen } from '@react-icons/all-files/ai/AiOutlineFullscreen';
import PageShell from '../components/design-system/foundations/PageShell';
import ServiceHero from '../components/design-system/marketing/ServiceHero';
import ServiceOverview from '../components/design-system/marketing/ServiceOverview';
import VisualDecisionGuide from '../components/design-system/marketing/VisualDecisionGuide';
import RentalFAQV2 from '../components/design-system/marketing/RentalFAQV2';
import RelatedServices from '../components/design-system/marketing/RelatedServices';
import QuotePop from '../components/DeferredQuotePopup';
import { FiMessageCircle } from '@react-icons/all-files/fi/FiMessageCircle';
import { FiShoppingBag } from '@react-icons/all-files/fi/FiShoppingBag';
import * as gtag from '../lib/gtag';
import { compactCatalogProducts, compactPublicCatalogPackages } from '../utilities/catalogPublicPayload';
import { compactPublicEvents } from '../utilities/eventPublicPayload';

const partyTentOverviewContent = {
    id: 'party-tent-rental-overview',
    heading: 'Choose a party tent by the site—not only by guest count',
    body: [
        'Start with the people and activities that must fit beneath the tent: seating, tables, buffet service, a dance floor, presentation space, equipment and circulation all use part of the covered area.',
        'Then confirm the installation surface, anchoring method, clear setup area, venue access, delivery window and weather plan. Those site details determine whether a tent can be installed and what the final delivery-and-setup price will be.'
    ],
    guide: {
        heading: 'What changes the final tent price?',
        summary: 'Tent dimensions are only one part of the estimate. Surface and anchoring, access, walls, location, delivery windows, installation labor, event dates and collection timing can all change the final price.',
        body: [
            <p key='filters'>Compare the live bundles by <strong>length</strong>, <strong>estimated capacity</strong>, <strong>covered area</strong> and equipment price. Capacity is a starting point, not a substitute for the actual floor plan.</p>,
            <p key='process'>Choose <strong>&quot;Choose Dates &amp; Rent Online&quot;</strong> to provide the location and timing. We will use those details to confirm availability, the appropriate anchoring plan, delivery, installation and collection.</p>
        ]
    },
    media: {
        src: tentSitePlanning,
        alt: 'Professionally installed white frame party tent with ballast, dining tables and clear circulation on a South Florida lawn'
    }
}

const tentPlanningItems = [
    {
        label: 'Step 1',
        title: 'Size the complete layout',
        icon: MdGroup,
        summary: 'Choose the tent around the usable floor plan rather than a guest-count number alone.',
        points: [
            'Count seated or standing guests and the table layout.',
            'Allow space for buffet lines, bars, entertainment and circulation.',
            'Include any stage, dance floor or production equipment in the footprint.'
        ]
    },
    {
        label: 'Step 2',
        title: 'Confirm the surface and anchoring',
        icon: AiOutlineFullscreen,
        summary: 'Ground anchors and concrete ballast require different site conditions and working clearances.',
        points: [
            'Identify grass, soil, asphalt, concrete, pavers or another surface.',
            'Confirm venue approval, underground utilities and permitted anchor locations.',
            'Reserve clear space for bases, ballast, entrances and service access.'
        ]
    },
    {
        label: 'Step 3',
        title: 'Plan access, weather and timing',
        icon: FiMessageCircle,
        summary: 'A tent reduces exposure, but no temporary tent makes an event suitable for every weather condition.',
        points: [
            'Provide installation and collection windows plus loading access.',
            'Identify walls, entrances, power, lighting and adjacent equipment.',
            'Follow the approved weather and wind plan before and during the event.'
        ]
    }
]

export const getStaticProps = wrapper.getStaticProps(
    async ({ store, preview }) => {
        const res = await axios.request(`${SERVER_IP}/inventory/pack/stages`)
        const corp = await axios.request(`${SERVER_IP}/user/static-landing`)
                const faq = await axios.request(`${SERVER_IP}/faq?category=Tents`)
        const products = await axios.request(`${SERVER_IP}/inventory/product?msr=true&cat=Tent`)
        store.dispatch({ type: productsReceived.type, payload: compactCatalogProducts(products.data) })
        store.dispatch({ type: productsSetFilters.type, payload: { static: true } })
        store.dispatch({ type: packagesReceived.type, payload: compactPublicCatalogPackages(res.data) })

        let { newsletterevents, total_events } = corp?.data
        return {
            props: {
                newsletterevents: compactPublicEvents(newsletterevents), total_events, faq: faq.data
            }
        }
    }
);



const StageRental = ({ newsletterevents, total_events, faq }) => {
    let products = useSelector(getFilteredProducts)
    let dispatch = useDispatch()
    return <>
           <BreadcrumbJsonLd
        itemListElements={[
          { position: 1, name: "Home", item: `https://miamisoundrental.com` },
      
          { position: 2, name: "Party Tent Rental", item: `https://miamisoundrental.com/party-tent-rental` },
        ]}
      />
        <NextSeo
            title='Party Tent Rental from $200 in Miami & Fort Lauderdale'
            description='Compare party and event tent rentals in Miami and Fort Lauderdale. Choose the size, surface, dates, delivery and professional setup for your event.'
            canonical="https://miamisoundrental.com/party-tent-rental"
            twitter={{
                handle: '@miamisoundrent1',
                site: '@miamisoundrent1',
                cardType: 'summary_large_image',
            }}
            openGraph={{
                type: 'website',
                url: `https://miamisoundrental.com/party-tent-rental`,
                title: 'Party Tent Rental from $200 in Miami & Fort Lauderdale',
                description: 'Compare party and event tent rentals in Miami and Fort Lauderdale. Choose the size, surface, dates, delivery and professional setup for your event.',
                images: [
                    {
                        url: 'https://dnw-server.s3.us-east-2.amazonaws.com/stage_og.jpg',
                        width: 800,
                        height: 600,
                        alt: 'Miami Sound Rental',
                    },

                ],
                site_name: 'Miami Sound Rental',
            }}
        />
        <div className='stageRental categoryRental corporateEvents'>
            <PageShell>
                <ServiceHero
                        title='Party Tent Rental in Miami & Fort Lauderdale'
                        description='Compare party and event tent sizes for weddings, celebrations, schools, organizations and outdoor programs. Choose the site, surface, dates, delivery and professional setup.'
                        image={{
                            src: '/Pictures/categories-v2/party-tent-rental-hero-generated-v2.jpg',
                            position: '70% 50%'
                        }}
                        imageAlt='Long white frame tent prepared for an outdoor evening reception in Miami'
                        primaryAction={{
                            label: 'Browse Tent Options',
                            href: '#bundles',
                            icon: FiShoppingBag,
                            conversionKey: 'party-tent-rental.hero.browse-tents',
                            channel: 'rental',
                            salesPath: 'self_service',
                            onClick: () => gtag.event({ action: 'catalog_click', label: 'leads', value: 2, category: 'orders' })
                        }}
                        secondaryAction={{
                            label: 'Plan Delivery & Setup',
                            icon: FiMessageCircle,
                            conversionKey: 'party-tent-rental.hero.plan-delivery',
                            channel: 'quote',
                            salesPath: 'managed',
                            onClick: () => {
                                gtag.event({ action: 'quote_click', label: 'leads', value: 3, category: 'orders' })
                                dispatch(SHOW_POPUP({ key: 'quote', size: 'medium', body: <QuotePop /> }))
                            }
                        }}
                        stackActionsOnMobile
                />
            </PageShell>
            <div className="stageType">
                <PageShell>
                    <ServiceOverview content={partyTentOverviewContent} />
                </PageShell>


                <section  id='bundles'>


                    <ProductList 
                          carousel={{
                    name: 'Party Tent Rentals',
                    url: 'https://miamisoundrental.com/party-tent-rental',
                    description: 'Party Tent bundles'
                }}
                    sortBy={{

                        'Capacity': ['green', <MdGroup key='group' style={{ color: 'green' }} />],
                        'Area': ['darkGoldenRod', <AiOutlineFullscreen key='group' style={{ color: 'darkGoldenRod' }} />],
                        Length: ['darkSlateBlue', <AiOutlineColumnHeight key='bolt' style={{ color: 'darkSlateBlue' }} />],
                    }} listName='Party Tents' anchor products={products.filter(p =>(p.name.includes("20'") || (p.name.includes("Wall")&& !p.name.includes("Bar")))&& p.categories?.includes('Staging/Tent'))} />
                    <NotSure />
                </section>
                <section className='stageType'>
                    <LandingCategories ui='v2' loading='lazy' className='padBottom padTop' options category='staging' heading='Explore other AV Categories' />

                </section>
                <VisualDecisionGuide
                    id='party-tent-planning-guide'
                    eyebrow='Plan the installation'
                    heading='Three decisions to make before renting a party tent'
                    description='These details help us recommend the appropriate tent, installation method and delivery plan without guessing from guest count alone.'
                    items={tentPlanningItems}
                />


            </div>







        <RentalFAQV2 questions={faq} headingId='party-tent-rental-faq-title' />

            <PageShell>
                <RelatedServices
                    eyebrow='Continue the site plan'
                    heading='Coordinate the tent with the physical event systems'
                    headingId='related-staging-rentals'
                    description='Use these canonical Staging-family pages when the event also needs a platform, a finished stage edge, a drape line or a truss structure.'
                    additionalServices={[
                        { h1: 'Stage Rental', url: '/stage-rental', miniText: 'Compare modular stage packages or configure a platform around the event footprint.' },
                        { h1: 'Stage Skirting & Floor Protection', url: '/staging/accessories', miniText: 'Match stage skirting to the finished deck and plan floor protection separately.' },
                        { h1: 'Pipe & Drape Rental', url: '/staging/drape', miniText: 'Plan backdrops, room dividers, masking walls and control-position surrounds.' },
                        { h1: 'Stage Truss Rental', url: '/staging/truss', miniText: 'Browse compatible components or request a managed event truss structure.' }
                    ]}
                />
            </PageShell>
            <PastEvents ui='v2' filter={['private', 'presentation', 'concert', 'party']} newsletterevents={newsletterevents} total_events={total_events} />
            <ClientReviews ui='v2' filter={['convenient', 'empower']} />

        </div>
    </>
}

export default StageRental
